import { useState, useMemo, useRef } from "react";
import {
  CommandPalette,
  type CommandPaletteItem,
  type CommandPaletteReport,
} from "@/components/CommandPalette";
import { useReportConfig } from "@/hooks/useReportConfig";
import { Link, Outlet, useLocation, useNavigate } from "react-router";
import {
  useSidebarConfig,
  useModuleAccess,
  useIsMdm,
  useNetworkStatus,
  useSpeedTest,
} from "@pos/shared";
import {
  useAuthStore,
  useBillingStore,
  useTableOrderStore,
} from "@pos/shared/stores";
import { logout } from "@pos/shared/api";
import { useQueryClient } from "@pos/shared/query";
import { writeAuditLog } from "@pos/shared/lib/audit";
import { TX } from "@pos/shared/types";
import { ROUTE_MODULE_MAP } from "@/config/route-module-map";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLinkItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
  Toaster,
  NetworkStatusBar,
} from "@pos/ui";
import {
  BarChart3,
  User,
  Settings,
  LogOut,
  PanelLeftClose,
  PanelLeft,
  Wifi,
  WifiOff,
  Ticket,
  PackagePlus,
  Search,
  Pin,
  PinOff,
  Tags,
} from "lucide-react";
import { MODULE_ICON_MAP, DEFAULT_ICON } from "@/config/module-icons";
import {
  SIDEBAR_CATEGORIES,
  SIDEBAR_TITLE_OVERRIDES,
} from "@/config/sidebar-categories";
import { modShortcut } from "@/lib/platform";

const isElectron = "electron" in window;
const isMac = isElectron && navigator.platform.startsWith("Mac");

// Non-production env badge — shows env tag + API URL. Hidden in
// production. Tone follows the env: amber for dev, sky for staging.
const APP_ENV = (import.meta.env.VITE_APP_ENV ?? "development").toLowerCase();
const ENV_BADGE: { label: string; classes: string } | null =
  APP_ENV === "production"
    ? null
    : APP_ENV === "staging" || APP_ENV === "stag"
      ? { label: "STAG", classes: "bg-sky-500/15 text-sky-600" }
      : { label: "DEV", classes: "bg-amber-500/15 text-amber-600" };

/**
 * Builds a route path from a module identifier.
 * Reverses the ROUTE_MODULE_MAP to find the path for a given module.
 */
const moduleToRoute: Record<string, string> = Object.fromEntries(
  Object.entries(ROUTE_MODULE_MAP).map(([path, mod]) => [mod, path]),
);

// Pilot-mode sidebar whitelist. When non-null the sidebar shows only
// these module IDs (plus the static MDM entries, when applicable).
// Set to `null` to restore the full backend-driven sidebar.
//
// Pilot nav currently OFF — full sidebar restored. To re-enable the
// pilot, swap the `null` below for the commented Set:
// const PILOT_SIDEBAR_WHITELIST: Set<string> | null = new Set([
//   "uidashboard",
//   "uitableorder",
//   "uitableorderconfig",
//   "uicontrolmanagement",
// ]);
const PILOT_SIDEBAR_WHITELIST: Set<string> | null = null;

function isActive(pathname: string, to: string): boolean {
  if (to === "/") return pathname === "/";
  return pathname === to || pathname.startsWith(to + "/");
}

export function RootLayout() {
  const { token, deviceId, user, store, clearSession } = useAuthStore();
  const queryClient = useQueryClient();
  const sidebarConfig = useSidebarConfig();
  const { hasModule } = useModuleAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const [loggingOut, setLoggingOut] = useState(false);
  const [collapsed, setCollapsed] = useState(true);

  // Favorites (pinned modules)
  const [favorites, setFavorites] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem("pos-sidebar-favorites");
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch {
      return [];
    }
  });

  const toggleFavorite = (path: string) => {
    setFavorites((prev) => {
      const next = prev.includes(path)
        ? prev.filter((p) => p !== path)
        : [...prev, path];
      localStorage.setItem("pos-sidebar-favorites", JSON.stringify(next));
      return next;
    });
  };
  const networkStatus = useNetworkStatus();
  const { running: speedTesting, latencyMs, runSpeedTest } = useSpeedTest();
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Re-show banner automatically when status changes to offline
  const prevStatusRef = useRef(networkStatus);
  if (prevStatusRef.current !== networkStatus) {
    if (networkStatus === "offline") setBannerDismissed(false);
    prevStatusRef.current = networkStatus;
  }

  // Build dynamic nav items from backend sidebarTitle config,
  // filtered by the user's allowed modules
  const isMdm = useIsMdm();

  type NavItem = { to: string; label: string; icon: typeof BarChart3 };
  type NavGroup = { label: string; items: NavItem[] };

  const navGroups = useMemo(() => {
    // Collect all visible modules
    const visibleModules = new Map<string, NavItem>();

    // Static items — pilot whitelist hides Group Dashboard along with
    // the dynamic modules. MDM-only entries stay visible regardless,
    // since support / onboarding tools are operator-internal.
    if (!PILOT_SIDEBAR_WHITELIST) {
      visibleModules.set("__group_dashboard", {
        to: "/group-dashboard",
        label: "Group Dashboard",
        icon: BarChart3,
      });
    }
    if (isMdm) {
      visibleModules.set("__store_onboarding", {
        to: "/store-onboarding",
        label: "Store Onboarding",
        icon: PackagePlus,
      });
      visibleModules.set("__support_tickets", {
        to: "/support-desk/tickets",
        label: "Support Tickets",
        icon: Ticket,
      });
      visibleModules.set("__mdm_price_update", {
        to: "/mdm/price-update",
        label: "MDM Price Update",
        icon: Tags,
      });
      visibleModules.set("__analytics_registry", {
        to: "/analytics/registry",
        label: "Analytics Registry",
        icon: BarChart3,
      });
    }

    // Dynamic modules from backend config
    for (const [moduleId, item] of Object.entries(sidebarConfig)) {
      if (PILOT_SIDEBAR_WHITELIST && !PILOT_SIDEBAR_WHITELIST.has(moduleId)) {
        continue;
      }
      if (item.view && hasModule(moduleId)) {
        visibleModules.set(moduleId, {
          to: moduleToRoute[moduleId] ?? `/${moduleId}`,
          label: SIDEBAR_TITLE_OVERRIDES[moduleId] ?? item.title,
          icon: MODULE_ICON_MAP[moduleId] ?? DEFAULT_ICON,
        });
      }
    }

    // Group into categories
    const groups: NavGroup[] = [];
    const placed = new Set<string>();

    for (const cat of SIDEBAR_CATEGORIES) {
      const catItems: NavItem[] = [];
      for (const moduleId of cat.modules) {
        const item = visibleModules.get(moduleId);
        if (item) {
          catItems.push(item);
          placed.add(moduleId);
        }
      }
      if (catItems.length > 0) {
        groups.push({ label: cat.label, items: catItems });
      }
    }

    // Hidden modules — not shown in sidebar
    const hiddenModules = new Set(["uiksbclledger", "uibankledger"]);

    // Uncategorized items (static + any modules not in categories)
    const uncategorized: NavItem[] = [];
    for (const [key, item] of visibleModules) {
      if (!placed.has(key) && !hiddenModules.has(key)) uncategorized.push(item);
    }
    if (uncategorized.length > 0) {
      groups.unshift({ label: "Overview", items: uncategorized });
    }

    return groups;
  }, [sidebarConfig, hasModule, isMdm]);

  // Command palette items
  const paletteItems: CommandPaletteItem[] = useMemo(
    () =>
      navGroups.flatMap((g) =>
        g.items.map((item) => ({
          path: item.to,
          label: item.label,
          icon: item.icon,
          group: g.label || "General",
        })),
      ),
    [navGroups],
  );

  // Report items for command palette
  const { groups: reportGroups } = useReportConfig();
  const paletteReports: CommandPaletteReport[] = useMemo(
    () =>
      reportGroups.flatMap((g) =>
        g.reports.map((r) => ({
          key: r.key,
          name: r.definition.map,
          group: g.name,
        })),
      ),
    [reportGroups],
  );

  // Flat list for page title lookup
  const navItems = useMemo(
    () => navGroups.flatMap((g) => g.items),
    [navGroups],
  );

  // Build page title from current nav items + utility routes
  const pageTitle = useMemo(() => {
    const titles: Record<string, string> = {
      "/group-dashboard": "Group Dashboard",
      "/support-desk": "Support Desk",
      "/profile": "Profile",
      "/settings": "Settings",
      "/invoices": "Invoices",
      "/tickets": "Tickets",
      "/support-desk/tickets": "Support Tickets",
      "/store-onboarding": "Store Onboarding",
    };
    for (const item of navItems) {
      titles[item.to] = item.label;
    }
    return titles;
  }, [navItems]);

  function getPageTitle(pathname: string): string {
    return pageTitle[pathname] ?? "Dashboard";
  }

  const userName = user?.userName ?? "User";
  const userType = user?.userType ?? "";
  const storeName = store?.shopName ?? "Store";
  const customerId = store?.customerId ?? "";
  const userInitial = userName.charAt(0).toUpperCase();

  async function handleLogout() {
    setLoggingOut(true);
    try {
      if (token && deviceId) {
        await logout();
      }
    } catch {
      // Network errors are acceptable — still clear local session
    } finally {
      writeAuditLog(1, "S", TX.LOGOUT, "Logout", "User logged out");
      clearSession();
      useBillingStore.getState().reset();
      useTableOrderStore.getState().clearSelection();
      queryClient.clear();
      navigate("/login", { replace: true });
    }
  }

  return (
    <TooltipProvider>
      <div className="flex h-screen bg-background overflow-hidden">
        {/* Sidebar */}
        <aside
          className={`flex flex-col bg-muted/30 transition-[width] duration-200 ease-in-out ${
            collapsed ? "w-16" : "w-56"
          }`}
        >
          {/* Sidebar header — reserves space for the macOS traffic
              lights when Electron is frameless, holds the logo +
              collapse toggle in the browser, and collapses to zero
              height on Windows/Linux Electron where there's no
              traffic-light gutter to fill. */}
          <div
            className={`flex shrink-0 items-center ${
              isElectron
                ? isMac
                  ? "h-14 bg-background border-b border-border [-webkit-app-region:drag]"
                  : "h-0"
                : `h-14 border-b border-r border-border ${collapsed ? "justify-center px-3" : "justify-between px-3"}`
            }`}
          >
            {!isElectron && !collapsed && (
              <img src="/logo.png" alt="XPOS" className="h-7 object-contain" />
            )}
            {!isElectron && (
              <button
                onClick={() => setCollapsed((c) => !c)}
                className={`flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer`}
              >
                {collapsed ? (
                  <PanelLeft className="size-4" />
                ) : (
                  <PanelLeftClose className="size-4" />
                )}
              </button>
            )}
          </div>

          {/* Nav items */}
          <nav className="scrollbar-thin flex-1 flex flex-col border-r border-border px-2 py-3 overflow-y-auto overflow-x-hidden">
            {/* Cmd+K search hint */}
            {!collapsed && (
              <button
                onClick={() => {
                  window.dispatchEvent(
                    new KeyboardEvent("keydown", {
                      key: "k",
                      metaKey: true,
                    }),
                  );
                }}
                className="mx-1 mb-3 flex items-center gap-2 rounded-lg border border-border/50 px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <Search className="size-3.5" />
                <span className="flex-1 text-left">Search...</span>
                <kbd className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono">
                  {modShortcut("K")}
                </kbd>
              </button>
            )}

            {/* Favorites */}
            {favorites.length > 0 && (
              <div className={!collapsed ? "mb-3" : ""}>
                {!collapsed && (
                  <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                    Favorites
                  </div>
                )}
                {collapsed && <div className="mx-2 mb-2 h-px bg-border" />}
                <div className="flex flex-col gap-0.5">
                  {favorites
                    .map((path) => navItems.find((i) => i.to === path))
                    .filter(Boolean)
                    .map((item) => {
                      const Icon = item!.icon;
                      const active = isActive(location.pathname, item!.to);

                      const link = (
                        <Link
                          key={`fav-${item!.to}`}
                          to={item!.to}
                          className={`flex items-center rounded-lg py-2 text-sm transition-colors ${
                            collapsed ? "justify-center px-0" : "gap-3 px-3"
                          } ${
                            active
                              ? "bg-primary/10 text-primary font-medium"
                              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                          }`}
                        >
                          <Icon className="size-4 shrink-0" />
                          {!collapsed && (
                            <span className="truncate">{item!.label}</span>
                          )}
                        </Link>
                      );

                      if (collapsed) {
                        return (
                          <Tooltip key={`fav-${item!.to}`}>
                            <TooltipTrigger render={<div />}>
                              {link}
                            </TooltipTrigger>
                            <TooltipContent side="right">
                              {item!.label}
                            </TooltipContent>
                          </Tooltip>
                        );
                      }

                      return link;
                    })}
                </div>
              </div>
            )}

            {/* Groups */}
            {navGroups.map((group, gi) => (
              <div
                key={group.label || gi}
                className={gi > 0 && !collapsed ? "mt-4" : ""}
              >
                {group.label && !collapsed && (
                  <div className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                    {group.label}
                  </div>
                )}
                {group.label && collapsed && gi > 0 && (
                  <div className="mx-2 my-2.5 h-px bg-border" />
                )}
                <div className="flex flex-col gap-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(location.pathname, item.to);
                    const isFav = favorites.includes(item.to);

                    const link = (
                      <div
                        key={item.to}
                        className={`group/nav flex items-center overflow-hidden rounded-lg text-sm transition-colors ${
                          active
                            ? "bg-primary/10 text-primary font-medium"
                            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                        }`}
                      >
                        <Link
                          to={item.to}
                          className={`flex min-w-0 flex-1 items-center py-2 ${
                            collapsed ? "justify-center px-0" : "gap-3 px-3"
                          }`}
                        >
                          <Icon className="size-4 shrink-0" />
                          {!collapsed && (
                            <span className="truncate">{item.label}</span>
                          )}
                        </Link>
                        {!collapsed && (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              toggleFavorite(item.to);
                            }}
                            className={`mr-2 shrink-0 rounded p-0.5 transition-opacity ${
                              isFav
                                ? "text-primary opacity-100"
                                : "opacity-0 group-hover/nav:opacity-50 hover:!opacity-100"
                            }`}
                          >
                            {isFav ? (
                              <PinOff className="size-3" />
                            ) : (
                              <Pin className="size-3" />
                            )}
                          </button>
                        )}
                      </div>
                    );

                    if (collapsed) {
                      return (
                        <Tooltip key={item.to}>
                          <TooltipTrigger render={<div />}>
                            {link}
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            {item.label}
                          </TooltipContent>
                        </Tooltip>
                      );
                    }

                    return link;
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* Sidebar footer — settings */}
          <div className="shrink-0 border-t border-r border-border px-2 py-3">
            {collapsed ? (
              <Tooltip>
                <TooltipTrigger render={<div />}>
                  <Link
                    to="/settings"
                    className={`flex h-9 items-center justify-center rounded-lg text-sm transition-colors ${
                      isActive(location.pathname, "/settings")
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    }`}
                  >
                    <Settings className="size-4 shrink-0" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">Settings</TooltipContent>
              </Tooltip>
            ) : (
              <Link
                to="/settings"
                className={`flex h-9 items-center gap-3 rounded-lg px-3 text-sm transition-colors ${
                  isActive(location.pathname, "/settings")
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                }`}
              >
                <Settings className="size-4 shrink-0" />
                <span className="truncate">Settings</span>
              </Link>
            )}
          </div>
        </aside>

        {/* Main content area */}
        <div className="flex flex-1 flex-col min-w-0">
          {/* Topbar — draggable in Electron */}
          <header
            className={`flex h-14 items-center justify-between border-b border-border px-6 ${
              isElectron ? "[-webkit-app-region:drag]" : ""
            }`}
          >
            {/* Left: toggle + page title */}
            <div className="flex items-center gap-3 [-webkit-app-region:no-drag]">
              {isElectron && (
                <button
                  onClick={() => setCollapsed((c) => !c)}
                  className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
                >
                  {collapsed ? (
                    <PanelLeft className="size-4" />
                  ) : (
                    <PanelLeftClose className="size-4" />
                  )}
                </button>
              )}
              <h1 className="text-xl font-semibold text-foreground">
                {getPageTitle(location.pathname)}
              </h1>
              <span className="text-xs font-medium text-muted-foreground/70 tabular-nums">
                v{__APP_VERSION__}
              </span>
              {ENV_BADGE && (
                <span
                  className={`rounded-md px-2 py-0.5 text-xs font-medium ${ENV_BADGE.classes}`}
                >
                  {ENV_BADGE.label} — {import.meta.env.VITE_API_URL}
                </span>
              )}
            </div>

            {/* Right: store + profile */}
            <div className="flex items-center gap-3 [-webkit-app-region:no-drag]">
              <span className="text-base text-muted-foreground hidden sm:flex items-center gap-2">
                {storeName}
                {customerId && (
                  <span className="font-mono text-[10px] font-medium tracking-wider uppercase rounded border border-border bg-muted px-1.5 py-0.5 text-muted-foreground">
                    {customerId}
                  </span>
                )}
              </span>

              {/* Network status indicator — always visible */}
              <Tooltip>
                <TooltipTrigger render={<div />}>
                  <button
                    onClick={runSpeedTest}
                    disabled={speedTesting}
                    className="relative flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    title={
                      networkStatus === "offline"
                        ? "Offline — click to recheck"
                        : networkStatus === "slow"
                          ? "Slow connection — click to test"
                          : "Online — click to run speed test"
                    }
                  >
                    {networkStatus === "offline" ? (
                      <WifiOff className="size-4 text-destructive" />
                    ) : (
                      <Wifi
                        className={`size-4 ${
                          networkStatus === "slow"
                            ? "text-amber-500"
                            : "text-emerald-500"
                        }`}
                      />
                    )}
                    {/* Pulsing ring for offline/slow */}
                    {networkStatus !== "online" && (
                      <span
                        className={`absolute inset-0 rounded-md animate-ping opacity-20 ${
                          networkStatus === "offline"
                            ? "bg-destructive"
                            : "bg-amber-500"
                        }`}
                      />
                    )}
                    {speedTesting && (
                      <span className="absolute inset-0 rounded-md animate-pulse bg-current opacity-10" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {speedTesting
                    ? "Testing connection..."
                    : networkStatus === "offline"
                      ? "Offline — click to recheck"
                      : networkStatus === "slow"
                        ? `Slow connection${latencyMs != null ? ` (${latencyMs} ms)` : ""} — click to retest`
                        : latencyMs != null
                          ? `Online (${latencyMs} ms) — click to retest`
                          : "Click to test connection speed"}
                </TooltipContent>
              </Tooltip>

              {/* Analytics header icon — POC entry point for the
                  Superset-embedded dashboard. Hidden during the pilot
                  alongside the Tickets icon. */}
              {!PILOT_SIDEBAR_WHITELIST && (
                <Tooltip>
                  <TooltipTrigger render={<div />}>
                    <Link
                      to="/analytics"
                      className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
                      title="Analytics"
                    >
                      <BarChart3 className="size-4" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Analytics</TooltipContent>
                </Tooltip>
              )}

              {/* Tickets header icon — hidden during the pilot.
                  Restore the block when ticket workflow re-enters
                  the operator surface. */}
              {!PILOT_SIDEBAR_WHITELIST && (
                <Tooltip>
                  <TooltipTrigger render={<div />}>
                    <Link
                      to="/tickets"
                      className="flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors cursor-pointer"
                      title="Tickets"
                    >
                      <Ticket className="size-4" />
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="bottom">Tickets</TooltipContent>
                </Tooltip>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 rounded-lg px-2 py-1 outline-hidden cursor-pointer hover:bg-accent/50 transition-colors">
                  <span className="flex size-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-base font-medium">
                    {userInitial}
                  </span>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="bottom" align="end">
                  <DropdownMenuLabel>
                    <div className="font-medium">{userName}</div>
                    {userType && (
                      <div className="text-sm text-muted-foreground">
                        {userType}
                      </div>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuLinkItem
                    href="/profile"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/profile");
                    }}
                  >
                    <User className="size-4" />
                    Profile
                  </DropdownMenuLinkItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleLogout}
                    disabled={loggingOut}
                  >
                    <LogOut className="size-4" />
                    {loggingOut ? "Logging out..." : "Logout"}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </header>

          {/* Network status banner */}
          {!bannerDismissed && (
            <NetworkStatusBar
              status={networkStatus}
              latencyMs={latencyMs}
              testing={speedTesting}
              onRunTest={runSpeedTest}
              onDismiss={() => setBannerDismissed(true)}
            />
          )}

          {/* Page content */}
          <main className="flex-1 overflow-y-auto">
            <Outlet />
          </main>
        </div>
      </div>
      <CommandPalette items={paletteItems} reports={paletteReports} />
      <Toaster position="top-right" richColors />
    </TooltipProvider>
  );
}
